import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Briefcase, ChevronRight, Inbox, Plus, Search } from 'lucide-react';
import type { CreateJobInput, Job, JobStatus } from '@portal/shared';
import {
  AppShell,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Input,
  Modal,
  Select,
  Spinner,
  Table,
  Textarea,
  useToast,
  type TableColumn,
} from '../../components/index.js';
import { api, ApiError } from '../../lib/api-client.js';

const STATUS_VARIANT: Record<JobStatus, 'neutral' | 'info' | 'success' | 'accent' | 'warning'> = {
  draft: 'neutral',
  active: 'info',
  priced: 'success',
  delivered: 'accent',
  archived: 'warning',
};

export function JobsTool() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | JobStatus>('all');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (search.trim()) params.set('q', search.trim());
      const qs = params.toString();
      const res = await api.get<{ jobs: Job[] }>(`/api/jobs${qs ? `?${qs}` : ''}`);
      setJobs(res.jobs);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError || err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  // Debounce search-as-you-type
  useEffect(() => {
    const t = setTimeout(() => {
      void refresh();
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const columns = useMemo<TableColumn<Job>[]>(
    () => [
      {
        id: 'business',
        header: 'Business',
        cell: (j) => (
          <div className="min-w-0">
            <div className="font-medium text-text-primary truncate">{j.business}</div>
            {j.client && <div className="text-sm text-text-secondary truncate">{j.client}</div>}
          </div>
        ),
      },
      {
        id: 'address',
        header: 'Address',
        cell: (j) => (
          <span className="text-sm text-text-secondary truncate block max-w-xs">
            {j.address || '—'}
          </span>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (j) => (
          <Badge variant={STATUS_VARIANT[j.status]} dot>
            {j.status}
          </Badge>
        ),
      },
      {
        id: 'created',
        header: 'Created',
        cell: (j) => (
          <span className="text-sm text-text-secondary tabular-nums">
            {new Date(j.createdAt).toLocaleDateString()}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-8 py-10 space-y-6">
        <Breadcrumb />
        <header className="flex items-end justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex size-12 items-center justify-center rounded-xl bg-accent-muted text-accent-hover">
              <Briefcase className="size-6" aria-hidden />
            </span>
            <div>
              <h1 className="text-3xl font-bold text-text-primary">Jobs</h1>
              <p className="mt-1 text-base text-text-secondary">
                Inspection jobs.
              </p>
            </div>
          </div>
          <Button
            type="button"
            leftIcon={<Plus className="size-4" aria-hidden />}
            onClick={() => setCreateOpen(true)}
          >
            New job
          </Button>
        </header>

        <Card className="p-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]">
              <Input
                label="Search"
                placeholder="Business or client…"
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                leftAdornment={<Search className="size-4" aria-hidden />}
              />
            </div>
            <div className="w-48">
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.currentTarget.value as 'all' | JobStatus)}
              >
                <option value="all">All statuses</option>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="priced">Priced</option>
                <option value="delivered">Delivered</option>
                <option value="archived">Archived</option>
              </Select>
            </div>
          </div>
        </Card>

        {loading && (
          <div className="flex justify-center py-10">
            <Spinner size="lg" />
          </div>
        )}

        {!loading && error && (
          <Card>
            <div role="alert" className="p-6 text-danger">
              Could not load jobs: {error}
            </div>
          </Card>
        )}

        {!loading && jobs && jobs.length === 0 && (
          <Card>
            <EmptyState
              icon={Inbox}
              title="No jobs yet"
              description={
                search || statusFilter !== 'all'
                  ? 'No jobs match the current filters. Try clearing them or creating a new job.'
                  : 'Click "New job" to create the first one.'
              }
            />
          </Card>
        )}

        {!loading && jobs && jobs.length > 0 && (
          <Card>
            <Table
              columns={columns}
              rows={jobs}
              rowKey={(j) => j.id}
              onRowClick={(j) => navigate(`/tools/jobs/${j.id}`)}
            />
          </Card>
        )}
      </div>

      <CreateJobModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={async (job) => {
          toast({ title: `Job created: ${job.business}`, variant: 'success' });
          setCreateOpen(false);
          await refresh();
        }}
      />
    </AppShell>
  );
}

interface CreateJobModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (job: Job) => void | Promise<void>;
}

function CreateJobModal({ open, onClose, onCreated }: CreateJobModalProps) {
  const { toast } = useToast();
  const [client, setClient] = useState('');
  const [business, setBusiness] = useState('');
  const [address, setAddress] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setClient('');
    setBusiness('');
    setAddress('');
    setNotes('');
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload: CreateJobInput = {
        business: business.trim(),
        client: client.trim() || undefined,
        address: address.trim() || undefined,
        notes: notes.trim() || undefined,
      };
      const res = await api.post<{ job: Job }>('/api/jobs', payload);
      reset();
      await onCreated(res.job);
    } catch (err) {
      toast({
        title: 'Create failed',
        description: err instanceof Error ? err.message : String(err),
        variant: 'danger',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Modal
      open
      onClose={() => (submitting ? undefined : (reset(), onClose()))}
      title="New job"
      description="Create a new inspection job. Audio + items + pricing attach here."
      footer={
        <>
          <Button
            variant="secondary"
            onClick={() => {
              reset();
              onClose();
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button form="create-job-form" type="submit" loading={submitting}>
            Create job
          </Button>
        </>
      }
    >
      <form id="create-job-form" onSubmit={onSubmit} className="space-y-4">
        <Input
          label="Business"
          required
          placeholder="e.g. Panago #018, Jeju – Tofino"
          value={business}
          onChange={(e) => setBusiness(e.currentTarget.value)}
        />
        <Input
          label="Client"
          placeholder="Optional — bank or referrer"
          value={client}
          onChange={(e) => setClient(e.currentTarget.value)}
        />
        <Input
          label="Address"
          placeholder="e.g. 123 Granville St, Vancouver BC"
          value={address}
          onChange={(e) => setAddress(e.currentTarget.value)}
        />
        <Textarea
          label="Notes"
          rows={3}
          placeholder="Optional"
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
        />
      </form>
    </Modal>
  );
}

function Breadcrumb() {
  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-sm text-text-secondary">
      <Link to="/dashboard" className="hover:text-text-primary">
        Dashboard
      </Link>
      <ChevronRight className="size-4 text-text-tertiary" aria-hidden />
      <span className="text-text-primary">Jobs</span>
    </nav>
  );
}
